import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Gift, Copy, Check, MessageCircle } from 'lucide-react';
import { api } from '@/lib/api';

interface ReferralInfo {
  code: string;
  link: string;
}

/**
 * Card de indicação oficina→oficina (growth 8.1). O gestor compartilha o
 * link com `?ref=` e o cadastro da oficina indicada registra a origem —
 * visível no backoffice para a equipe MustAuto reconhecer quem indica.
 */
export function ReferralCard({ workshopId }: { workshopId: string | null }) {
  const [copied, setCopied] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['referral', workshopId],
    queryFn: async () => {
      const res = await api.get<ReferralInfo>('/workshops/my/referral');
      return res.data;
    },
    enabled: Boolean(workshopId),
    staleTime: Infinity,
  });

  if (!workshopId) return null;

  const shareMessage = data
    ? `Tô usando o MustAuto na minha oficina — é 100% grátis e o cliente recebe fotos e vídeo do serviço no celular. Vale conhecer: ${data.link}`
    : '';

  async function copyLink() {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard bloqueado — usuário ainda vê o link no card */ }
  }

  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <Gift size={18} className="text-primary" />
        <h2 className="text-base font-bold">Indique uma oficina</h2>
      </div>
      <p className="mt-1.5 text-sm text-muted-foreground">
        Conhece um dono de oficina que ia curtir isso? Manda seu link — quando a oficina
        entrar, fica registrado que a indicação foi sua.
      </p>

      {isLoading && (
        <div className="mt-3 h-9 w-full max-w-md rounded-md bg-muted-foreground/10 animate-pulse" />
      )}

      {isError && (
        <p className="mt-3 text-sm text-muted-foreground">
          Não foi possível carregar seu link.{' '}
          <button type="button" onClick={() => refetch()} className="font-medium text-primary underline">
            Tentar de novo
          </button>
        </p>
      )}

      {data && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <code className="flex-1 truncate rounded-md border bg-muted/40 px-3 py-2 text-xs">
            {data.link}
          </code>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted transition-colors"
            >
              {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
              {copied ? 'Copiado' : 'Copiar'}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
            >
              <MessageCircle size={14} />
              WhatsApp
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
